import { Box, Button, Card, CircularProgress, Grid, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { styled } from 'styled-components';
import {
  IComandasPorFase,
  IComandasPorPlano,
  IComandasPorStatus,
  IEtapasJaRealizadas
} from '../../commons/types/dashboard';
import { useGraficosActions } from '../../states/graficos/graficos.actions';
import { authAtom } from '../../states/usuarios/AuthState';
import { useUsuarioActions } from '../../states/usuarios/usuario.actions';
import ImgBackground from '../../assets/intoo_E.png';
import GaugeChart from './Components/GaugeChart';
import DonutChart from './Components/DonutChart';
import BarChart from './Components/BarChart';
import HorizontalBarChart from './Components/HorizontalBarChart';
import { useEmpresasActions } from '../../states/empresas/empresas.actions';
import DashboardsPDF from './Components/DashboardPDF';
import generatePDF, { Margin, Resolution } from 'react-to-pdf';
import { esES, ptBR } from '@mui/material/locale';
import { useTranslation } from '../../commons/useTranslasion';

const Dashboard: React.FC = () => {
    const { translate } = useTranslation();
    const lang = process.env["REACT_APP_LANG"] ?? "pt-br";
    let locale = ptBR;
    switch (lang.slice(0, 2)) {
      case "es":
        locale = esES
        break;
    }
  const userActions = useUsuarioActions();
  const empresasActions = useEmpresasActions();
  const user = useRecoilValue(authAtom);
  const actions = useGraficosActions();

  const [empresa, setEmpresa] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [comandasPorFase, setComandasPorFase] = useState<IComandasPorFase[]>([]);
  const [comandasPorStatus, setComandasPorStatus] = useState<IComandasPorStatus[]>([]);
  const [comandasPorPlano, setComandasPorPlano] = useState<IComandasPorPlano[]>([]);
  const [etapasFinalizadas, setEtapasFinalizadas] = useState<IEtapasJaRealizadas[]>([]);
  const [mediaRecolocacao, setMediaRecolocacao] = useState<number>(0);
  const [volumeIndicacoesVagas, setVolumeIndicacoesVagas] = useState<number>(0);

  const targetRef = useRef<HTMLDivElement>(null);

  const colorPalette = [
    '#1976d2',
    '#2196f3',
    '#0d47a1',
    '#4fc3f7',
    '#01579b',
    '#03a9f4',
    '#0288d1',
    '#039be5',
    '#0277bd',
    '#0091ea'
  ];

  const getComandasPorFase = () => {
    actions.getComandasPorFase()
      .then((data) => setComandasPorFase(data ?? []))
      .catch(() => toast.error(translate("falhaAoBuscaarComandasPorFase")))
  }

  const getEtapasFinalizadas = () => {
    actions.getEtapasJaRealizadas()
      .then((data) => setEtapasFinalizadas(data ?? []))
      .catch(() => toast.error(translate("falhaAoBuscarEtapasFinalizadas")))
  }

  const getMediaRecolocacao = () => {
    actions.getMediaRecolocacao()
      .then((data) => setMediaRecolocacao(data ?? 0))
      .catch(() => toast.error(translate("falhaAoBuscarMediaRecolocacao")))
  }

  const getVolumeIndicacoesVagas = () => {
    actions.getVolumeIndicacoesVagas()
      .then((data) => setVolumeIndicacoesVagas(data ?? 0))
      .catch(() => toast.error(translate("falhaAoBuscarVolumeIndicacoesVagas")))
  }

  const getComandasPorPlano = () => {
    actions.getComandasPorPlano()
      .then((data) => setComandasPorPlano(data ?? []))
      .catch(() => toast.error(translate("falhaAoBuscarComandasPorPlano")))
  }

  const getComandasPorStatus = () => {
    actions.getComandasPorStatus()
      .then((data) => setComandasPorStatus(data ?? []))
      .catch(() => toast.error(translate("falhaAoBuscarComandasPorStatus")))
  }

  const getEmpresa = () => {
    if (!user || !user?.empresaId) {
      toast.warn(translate("facaLoginParaTerAcessoAoSistema"));
      userActions.logout();
      window.location.href = '/login';

      return;
    }

    if (empresasActions) {
      empresasActions?.getEmpresa()
        .then((data) => {
          setEmpresa(data);
        })
        .catch(() => toast.error(translate("falhaAoBuscarEmpresa")));
    }
  }

  useEffect(() => {
    if (!user || !user.empresaId) {
      toast.warn(translate("facaLoginParaTerAcessoAoSistema"));
      userActions.logout();
      window.location.href = '/login';
      return;
    }

    setLoading(true);
    setTimeout(() => {
      getEmpresa();
      getMediaRecolocacao();
      getComandasPorFase();
      getComandasPorStatus();
      getComandasPorPlano();
      getVolumeIndicacoesVagas();
      getEtapasFinalizadas();
      setLoading(false);
    }, 2000)

  }, []);

  const getPorcentagemStatus = (status: string) => {
    const total = comandasPorStatus.reduce((acc, item) => acc + item.quantidade, 0);
    const quantidade = comandasPorStatus.find((item) => item.status == status)?.quantidade;
    return (quantidade ?? 0) / total * 100;
  };

  const getPorcentagemAdesao = (expectativa: number, impactados: number, adesao = true) => {
    if (expectativa <= 0) return 0;

    const taxaAdesao = (impactados / expectativa) * 100;
    const taxaNaoAdesao = 100 - taxaAdesao;

    return Number(adesao ? taxaAdesao : taxaNaoAdesao).toFixed(0);
  }

  const handleExportPDF = async () => {
    try {
      if (!targetRef.current) {
        throw new Error(translate("conteudoNaoEstaProntoParaExportacao"));
      }

      await generatePDF(() => targetRef.current!, {
        filename: 'STATO_Dashboard.pdf',
        method: 'save',
        resolution: Resolution.NORMAL,
        page: {
          margin: Margin.SMALL,
          format: 'a4',
          orientation: 'landscape'
        },
        canvas: {
          mimeType: 'image/jpeg',
          qualityRatio: 0.98
        },
        overrides: {
          pdf: {
            compress: true,
            unit: 'mm',
            format: 'a4'
          },
          canvas: {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            logging: false
          }
        }
      });
    } catch (error) {
      console.error(translate("erroAoGerarPdf"), error);
      toast.error(translate("falhaAoGerarPdf"));
    }
  }

  return (
    <>
      <DivBackground>
        {loading ? (
          <SpinnerContainer>
            <CircularProgress size={60} thickness={4} sx={{ color: '#1976d2' }} />
            {translate("carregandoInformacoesDoDashboard")}
          </SpinnerContainer>
        ) :
          (
            <DivDashboard style={{ marginBottom: '80px' }}>
              {empresa.logo && (
                <img src={empresa.logo} alt="Logo" style={{ width: '250px', height: 'auto', marginLeft: '25px' }} />
              )}

              <Typography style={{ marginLeft: '25px' }} variant="h1">
                {translate("dashboardEmpresa")}: {empresa.razaosocial}
              </Typography>

              <Box>
                <Button
                  variant="contained"
                  onClick={handleExportPDF}
                  sx={{ margin: '20px' }}
                >
                  {translate("exportarPdf")}
                </Button>
              </Box>

              <Box sx={{ flexGrow: 1, mt: 9 }}>
                <Grid
                  container
                  spacing={4}
                  sx={{ mb: 6 }}
                >
                  <Grid item xs={6}>
                    <StyledCard>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <MetricBox>
                            <MetricValue variant="h2">
                              {comandasPorStatus.reduce((acc, item: any) => acc + item.quantidade, 0)}
                            </MetricValue>
                            <MetricLabel variant="h5">{translate("adesao")}</MetricLabel>
                          </MetricBox>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MetricBox>
                            <MetricValue variant="h2">{comandasPorStatus.find((item: any) => item.status == translate('ativo'))?.quantidade ?? 0}</MetricValue>
                            <MetricLabel variant="h5">{translate("ativos")}</MetricLabel>
                          </MetricBox>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MetricBox>
                            <MetricValue variant="h2">{getPorcentagemAdesao(empresa?.estimativaAdesao ?? 0, comandasPorStatus.reduce((acc, item: any) => acc + item.quantidade, 0), true)}%</MetricValue>
                            <MetricLabel variant="h5">
                              {translate("deAdesao")} (Stato)
                            </MetricLabel>
                          </MetricBox>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <MetricBox>
                            <MetricValue variant="h2">{getPorcentagemAdesao(empresa?.estimativaAdesao ?? 0, comandasPorStatus.reduce((acc, item: any) => acc + item.quantidade, 0), false)}%</MetricValue>
                            <MetricLabel variant="h5">{translate("deNaoAdesao")}</MetricLabel>
                          </MetricBox>
                        </Grid>
                      </Grid>
                    </StyledCard>
                  </Grid>

                  <Grid item xs={2}>
                    <StyledCard>
                      <Typography variant="h5" sx={{ textAlign: 'center', fontWeight: 'bold', color: 'black' }}>
                        {translate("workshopsEEventosDedicados")}
                      </Typography>

                      <Typography
                        variant="h2"
                        sx={{
                          color: '#0d47a1',
                          textAlign: 'center',
                          marginTop: '100px',
                          fontSize: '80px'
                        }}
                      >
                        {empresa.eventosDedicados ?? 0}h
                      </Typography>
                    </StyledCard>
                  </Grid>

                  <Grid item xs={2}>
                    <GaugeCard>
                      <GaugeTitle style={{ marginTop: '25px', fontWeight: 'bold', color: 'black' }}>
                        {getPorcentagemStatus('Recolocado').toFixed(0)}% {translate("deRecolocacao")}
                      </GaugeTitle>
                      <GaugeDescription style={{ marginBottom: '45px' }}>

                        ({comandasPorStatus.find((item: any) => item.status == ('Recolocado'))?.quantidade ?? 0} {translate("dos")} {comandasPorStatus.reduce((acc, item: any) => acc + item.quantidade, 0)} {translate("adesao")} = {getPorcentagemStatus(translate("recolocado")).toFixed(0)}% {translate("deAdesao")}

                      </GaugeDescription>
                      <GaugeChartContainer>
                        <GaugeChart
                          value={Math.round(getPorcentagemStatus('Recolocado'))}	
                          color="#1976d2"
                        />
                      </GaugeChartContainer>
                    </GaugeCard>
                  </Grid>

                  <Grid item xs={2}>
                    <GaugeCard>
                      <GaugeTitle style={{ marginTop: '25px', fontWeight: 'bold', color: 'black' }}>
                        {mediaRecolocacao ?? 0} {translate("meses")}
                      </GaugeTitle>
                      <GaugeDescription>
                        {translate("tempoMedioPorRecolocacao")}
                      </GaugeDescription>
                      <GaugeChartContainer style={{ marginTop: '25px' }}>
                        <GaugeChart
                          value={mediaRecolocacao ?? 0}
                          color="#1976d2"
                          percent={false}
                        />
                      </GaugeChartContainer>
                    </GaugeCard>
                  </Grid>
                </Grid>

                <Grid container spacing={4} sx={{ mb: 6 }}>
                  <Grid item xs={4}>
                    <StyledCard>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        width: '100%',
                        height: '64px',
                      }}>
                        <MetricValue variant="h6" sx={{
                          position: 'absolute',
                          top: '24px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          width: '100%',
                        }}>
                          {translate("executivoPorFase")}
                        </MetricValue>
                      </Box>
                      <DonutChart data={comandasPorFase.sort((a, b) => a.fase.localeCompare(b.fase))} />
                    </StyledCard>
                  </Grid>

                  <Grid item xs={4}>
                    <StyledCard>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        width: '100%',
                        height: '64px',
                      }}>
                        <MetricValue variant="h6" sx={{
                          position: 'absolute',
                          top: '24px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          width: '100%',
                        }}>
                          {translate("programas")} 
                        </MetricValue>
                      </Box>
                      <BarChart
                        categories={comandasPorPlano.sort((a: any, b: any) => a.plano.localeCompare(b.plano)).map(item => item.plano)}
                        series={[{
                          name: 'Comandas',
                          data: comandasPorPlano.sort((a: any, b: any) => a.plano.localeCompare(b.plano)).map(item => item.quantidade),
                          color: colorPalette[0]
                        }]}
                        colors={colorPalette}
                      />
                    </StyledCard>
                  </Grid>

                  <Grid item xs={4}>
                    <StyledCard>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        width: '100%',
                        height: '64px',
                      }}>
                        <MetricValue variant="h6" sx={{
                          position: 'absolute',
                          top: '24px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          width: '100%',
                        }}>
                          {translate("statusDoProjeto")}
                        </MetricValue>
                      </Box>
                      <HorizontalBarChart
                        data={comandasPorStatus.sort((a: any, b: any) => a.status.localeCompare(b.status)).map(item => ({
                          category: item.status,
                          value: item.quantidade
                        }))}
                        height={300}
                      />
                    </StyledCard>
                  </Grid>
                </Grid>
              </Box>
            </DivDashboard>)
        }
      </DivBackground>

      {!loading && (
        <div
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '383mm',
            height: 'auto',
            backgroundColor: 'white',
            margin: 0,
            padding: 0
          }}
        >
          <div ref={targetRef} style={{ width: '100%', height: '100%' }}>
            <DashboardsPDF
              empresa={empresa}
              comandasPorStatus={comandasPorStatus}
              comandasPorFase={comandasPorFase}
              mediaRecolocacao={mediaRecolocacao}
              comandasPorPlano={comandasPorPlano}
              etapasFinalizadas={etapasFinalizadas}
              volumeIndicacoesVagas={volumeIndicacoesVagas}
              colorPalette={colorPalette}
            />
          </div>
        </div>
      )}
    </>
  );
};

const StyledCard = styled(Card)(() => ({
  padding: '16px',
  height: '100%',
  borderRadius: '12px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  border: '1px solid #eeeeee', // Add border
  transition: 'transform 0.2s ease-in-out',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  },
}));

const StyledCard2 = styled(Card)`
  position: relative;
  padding-top: 56px; 
  padding-left: 16px;
  padding-right: 16px;
  padding-bottom: 16px;
  height: 100%;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border: 1px solid #eeeeee; // Add border
  transition: transform 0.2s ease-in-out;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 90vh;
`;

const GaugeCard = styled(Card)`
  position: relative;
  padding: 16px;
  height: 100%;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border: 1px solid #eeeeee; // Add border
  transition: transform 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
`;

const GaugeTitle = styled(Typography)`
  font-size: 16px;
  font-weight: bold;
  color: #08467C;
  text-align: center;
`;

const GaugeDescription = styled(Typography)`
  font-size: 14px;
  color: #757575;
  text-align: center;
  margin-bottom: 8px;
`;

const GaugeChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
`;

const DivBackground = styled.div`
  background: var(--gray-background);
  min-height: 90vh;
`;

const DivDashboard = styled.div`
  background-image: url(${ImgBackground});
  background-repeat: no-repeat;
  background-position: top right;
  background-attachment: fixed;
  min-height: 80vh;
  padding: 64px 32px 48px;
`;

const MetricBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  padding: 16px;
  height: 100%;
  min-height: 80px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.8);
  transition: transform 0.2s ease-in-out;
  gap: 12px;
  &:hover {
    transform: translateY(-2px);
    background-color: rgba(255, 255, 255, 1);
  }
`;

const MetricValue = styled(Typography)`
  font-weight: bold;
  color: black !important;
  white-space: nowrap;
`;

const MetricLabel = styled(Typography)`
  color: black !important;
  word-wrap: break-word;
  flex: 1;
  line-height: 1.2;
`;

const HeaderCustom = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 32px 0px;
  gap: 16px;
  position: absolute;
  width: calc(100% + 2px);
  height: 40px;
  top: -1px;
  left: -1px;
  background: #F5FAFF;
  border-width: 1px 1px 0px 1px;
  border-style: solid;
  border-color: #E9EAEB;
  border-radius: 8px 8px 0px 0px;
  color: #08467C;
  font-weight: bold;
  text-align: center;
  border-bottom: 2px solid #08467C;
`;

export default Dashboard;